var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { reaction, observable, computed } from "mobx";
import { Persistence, SocialLoginAuthState } from "../Infra/Persistence";
import { NamedRoutes } from "./NamedRoutes";
import { LoginAnalyticsEvent } from "healthex-common/analytics.dtos";
import { getHeapGlobalEventProperties } from "../Infra/LoadHeap";
import { generateOAuthState, getAnonymousUserId, LoginFlow } from "../Logic/AuthManager";
import { GFIDProvider, isSocialLoginGfidProvider } from "healthex-common/healthcareorganization.dtos";
import { asEnum, willNeverHappen } from "healthex-common/utils";
import { isOk } from "healthex-common/result";
import { effectiveQueryParams } from "./queryParams";
export class SocialLoginAuthManager {
    constructor(authManager, persistence, routingUIManager, analyticsService, environment) {
        this.authManager = authManager;
        this.persistence = persistence;
        this.routingUIManager = routingUIManager;
        this.analyticsService = analyticsService;
        this.environment = environment;
        this.state = SocialLoginAuthState.INACTIVE;
        this._errorMessage = null;
        this.requestedGFIDProvider = null;
        this.responseGFIDProvider = null;
        reaction(() => this.routingUIManager.currentRoute, (currentRoute) => {
            if ((currentRoute === null || currentRoute === void 0 ? void 0 : currentRoute.routeName) === NamedRoutes.SOCIAL_LOGIN_CALLBACK.routeName) {
                void this.processSocialLoginCallback();
            }
        }, { fireImmediately: true, name: "SocialLoginAuthManager_currentRoute" });
        window.addEventListener("pageshow", (event) => {
            if (event.persisted) {
                this.resetAuthStateIfNeeded();
            }
        });
    }
    get errorMessage() {
        return this.state === SocialLoginAuthState.ERROR ? this._errorMessage : null;
    }
    get isAccountLinkingError() {
        return this.state === SocialLoginAuthState.ERROR && this._errorMessage === "CLEAR_ACCOUNT_LINKING_REQUIRED";
    }
    get hasCompletedAuth() {
        return this.state === SocialLoginAuthState.SUCCESS || this.state === SocialLoginAuthState.ERROR;
    }
    async redirectToSocialLoginPage(callbackHash, gfidProvider, source, organizationId) {
        var _a, _b;
        this.state = SocialLoginAuthState.IN_PROGRESS;
        this.requestedGFIDProvider = gfidProvider;
        const heapGlobalEventProperties = { ...getHeapGlobalEventProperties(), source, provider: gfidProvider };
        const userId = (_a = getAnonymousUserId()) !== null && _a !== void 0 ? _a : undefined;
        const identity = (_b = this.authManager.id) !== null && _b !== void 0 ? _b : undefined;
        void this.analyticsService.trackHeapEvent({
            event: LoginAnalyticsEvent.LOGIN_ATTEMPTED,
            identity,
            user_id: userId,
            properties: { ...heapGlobalEventProperties },
        });
        const resolvedOrganizationId = typeof organizationId === "function" ? await organizationId() : organizationId;
        const url = this.getSocialLoginUrl(gfidProvider);
        if (!url) {
            this.handleAuthError();
            return;
        }
        const state = generateOAuthState();
        this.setSocialLoginAuthData({
            callbackHash,
            state,
            heapGlobalEventProperties,
            organizationId: resolvedOrganizationId !== null && resolvedOrganizationId !== void 0 ? resolvedOrganizationId : undefined,
        });
        window.location.href = `${url}?state=${state}`;
    }
    static checkAndReturnSocialLoginAuthResponse() {
        var _a;
        const socialLoginAuthData = new Persistence().getSocialLoginAuthData();
        if (!socialLoginAuthData)
            return null;
        const queryParams = effectiveQueryParams();
        const gfidProvider = asEnum(GFIDProvider)((_a = queryParams.get("social-login-provider")) !== null && _a !== void 0 ? _a : "");
        const state = queryParams.get("state");
        const token = queryParams.get("token");
        const error = queryParams.get("error");
        const linkingToken = queryParams.get("linkingToken");
        const isFirstTimeLogin = queryParams.get("isFirstTimeLogin") === "true";
        if (!!gfidProvider && !!state && isSocialLoginGfidProvider(gfidProvider) && (!!token || !!error)) {
            return { gfidProvider, token, error, linkingToken, state, isFirstTimeLogin };
        }
        else {
            return null;
        }
    }
    resetAuth() {
        this.state = SocialLoginAuthState.INACTIVE;
        this.requestedGFIDProvider = null;
        this.persistence.setSocialLoginAuthData(null);
    }
    setSocialLoginAuthData(socialLoginAuthData) {
        this.persistence.setSocialLoginAuthData(socialLoginAuthData);
    }
    getSocialLoginAuthData() {
        return this.persistence.getSocialLoginAuthData();
    }
    async processSocialLoginCallback() {
        var _a, _b;
        const socialLoginAuthData = this.getSocialLoginAuthData();
        if (!socialLoginAuthData) {
            this.handleAuthError();
            return;
        }
        const socialLoginAuthResponse = SocialLoginAuthManager.checkAndReturnSocialLoginAuthResponse();
        if (!socialLoginAuthResponse) {
            this.handleAuthError();
            return;
        }
        if (!socialLoginAuthData.state || !socialLoginAuthResponse.state || socialLoginAuthData.state !== socialLoginAuthResponse.state) {
            this.handleAuthError();
            return;
        }
        const { token, gfidProvider, error, linkingToken, isFirstTimeLogin } = socialLoginAuthResponse;
        this.responseGFIDProvider = gfidProvider;
        const userId = (_a = getAnonymousUserId()) !== null && _a !== void 0 ? _a : undefined;
        if (!token || error) {
            this.handleAuthError(error, linkingToken, gfidProvider);
            void this.analyticsService.trackHeapEvent({
                event: LoginAnalyticsEvent.LOGIN_ERROR,
                user_id: userId,
                properties: { ...socialLoginAuthData.heapGlobalEventProperties, error },
            });
            return;
        }
        try {
            const result = await this.authManager.handleSocialLogin(token, this.getLoginFlow(gfidProvider), isFirstTimeLogin);
            if (isOk(result)) {
                this.handleAuthSuccess();
                void this.analyticsService.trackHeapEvent({
                    event: LoginAnalyticsEvent.LOGIN_SUCCESS,
                    identity: (_b = this.authManager.id) !== null && _b !== void 0 ? _b : undefined,
                    user_id: userId,
                    properties: { ...socialLoginAuthData.heapGlobalEventProperties },
                });
                if (this.authManager.id && userId) {
                    void this.analyticsService.identifyUser(this.authManager.id, userId);
                }
            }
            else {
                this.handleAuthError();
                void this.analyticsService.trackHeapEvent({
                    event: LoginAnalyticsEvent.LOGIN_ERROR,
                    user_id: userId,
                    properties: { ...socialLoginAuthData.heapGlobalEventProperties },
                });
            }
        }
        catch (e) {
            console.error(`Error authenticating with Social Login ${gfidProvider}`, e);
            this.handleAuthError();
            void this.analyticsService.trackHeapEvent({
                event: LoginAnalyticsEvent.LOGIN_ERROR,
                user_id: userId,
                properties: { ...socialLoginAuthData.heapGlobalEventProperties },
            });
        }
        window.history.replaceState(null, "", window.location.href.replace(window.location.search, ""));
    }
    handleAuthError(error, linkingToken, gfidProvider) {
        this.state = SocialLoginAuthState.ERROR;
        this._errorMessage = error !== null && error !== void 0 ? error : null;
        if (linkingToken) {
            this.setAccountLinkingData({ linkingToken, gfidProvider });
        }
    }
    handleAuthSuccess() {
        this.state = SocialLoginAuthState.SUCCESS;
    }
    resetAuthStateIfNeeded() {
        const socialLoginAuthData = this.getSocialLoginAuthData();
        if (socialLoginAuthData) {
            this.resetAuth();
        }
    }
    getSocialLoginUrl(gfidProvider) {
        if (gfidProvider === GFIDProvider.GOOGLE) {
            return `${this.environment.API_BASE_URL}/auth/google`;
        }
        else if (gfidProvider === GFIDProvider.MICROSOFT) {
            return `${this.environment.API_BASE_URL}/auth/microsoft`;
        }
        else if (gfidProvider === GFIDProvider.APPLE) {
            return `${this.environment.API_BASE_URL}/auth/apple`;
        }
        else if (gfidProvider === GFIDProvider.SMART_ON_FHIR ||
            gfidProvider === GFIDProvider.CLEAR ||
            gfidProvider === GFIDProvider.HX_TEST) {
            return null;
        }
        else {
            willNeverHappen(gfidProvider);
            return null;
        }
    }
    getLoginFlow(gfidProvider) {
        if (gfidProvider === GFIDProvider.GOOGLE) {
            return LoginFlow.GFID_GOOGLE;
        }
        else if (gfidProvider === GFIDProvider.MICROSOFT) {
            return LoginFlow.GFID_MICROSOFT;
        }
        else if (gfidProvider === GFIDProvider.APPLE) {
            return LoginFlow.GFID_APPLE;
        }
        else if (gfidProvider === GFIDProvider.SMART_ON_FHIR ||
            gfidProvider === GFIDProvider.CLEAR ||
            gfidProvider === GFIDProvider.HX_TEST) {
            return null;
        }
        else {
            willNeverHappen(gfidProvider);
            return null;
        }
    }
    setAccountLinkingData(accountLinkingData) {
        this.persistence.setAccountLinkingData(accountLinkingData);
    }
    getAccountLinkingData() {
        return this.persistence.getAccountLinkingData();
    }
}
__decorate([
    observable,
    __metadata("design:type", String)
], SocialLoginAuthManager.prototype, "state", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], SocialLoginAuthManager.prototype, "_errorMessage", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], SocialLoginAuthManager.prototype, "errorMessage", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], SocialLoginAuthManager.prototype, "isAccountLinkingError", null);
__decorate([
    observable,
    __metadata("design:type", Object)
], SocialLoginAuthManager.prototype, "requestedGFIDProvider", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], SocialLoginAuthManager.prototype, "responseGFIDProvider", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], SocialLoginAuthManager.prototype, "hasCompletedAuth", null);
